import { USERTYPE } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { MillOnboardingContext } from "contexts/MillOnboardingContext/MillOnboardingContext";
import { useContext, useMemo } from "react";

const defaultCorporationsPermissions: {
    showNotifications: boolean;
    showStepper: boolean;
    onlyGeneralInformation: boolean;
    onlyMillSettings: boolean;
    showHmiCode: boolean;
    showClusterCode: boolean;
    canEdit: boolean;
    canEditProductionLines: boolean;
    canEditMachineCenters: boolean;
    canToggleMachineCenterNotifications: boolean;
    canEditUsers: boolean;
    canEditSettings: boolean;
    canDelete: boolean;
    showSecondaryCodeRequirements: boolean;
    canRefreshHMICode: boolean;
    canDragAndDropMachineCenters: boolean;
    canRefreshClusterCode: boolean;
} = {
    showNotifications: false,
    showStepper: true,
    onlyGeneralInformation: false,
    onlyMillSettings: false,
    showHmiCode: false,
    showClusterCode: false,
    canEdit: false,
    canEditProductionLines: false,
    canEditMachineCenters: false,
    canToggleMachineCenterNotifications: false,
    canEditUsers: false,
    canEditSettings: false,
    canDelete: false,
    showSecondaryCodeRequirements: false,
    canRefreshHMICode: false,
    canDragAndDropMachineCenters: false,
    canRefreshClusterCode: false,
};

const defaultSidebarPermissions = {
    showSettingsLink: false,
    showCorporateDashboardLink: false,
    showAPIKeyManagement: false,
    canGenerateApiKey: false,
    canRevokeApiKey: false,
};

const defaultRCAPermissions = {
    issues: {
        canCreate: false,
        canDelete: false,
        canEdit: false,
        executiveMillsFilter: false,
    },
    action_plans: {
        canCreate: false,
        canDelete: false,
        canEdit: false,
        canApproveActionPlans: false,
    },
    rca_library: {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        executiveMillsFilter: false,
    },
    dashboard: {
        canCreateIssues: false,
        canCreateRca: false,
        canApproveActionPlans: false,
    },
    settings: {
        canEdit: false,
    },
};

const useRolesPermissions = (rcaModificationAllowedIds?: number[]) => {
    const { currentUser, enabledNotifications } = useContext(CorporateContext);
    const { isMillOnboarding } = useContext(MillOnboardingContext);
    const currentUserType = useMemo(() => currentUser?.user_type?.user_type_id, [currentUser]);

    const userHasOwnership = useMemo(
        () => Boolean(rcaModificationAllowedIds?.some((id) => id === currentUser?.user_id)),
        [rcaModificationAllowedIds, currentUser],
    );

    const CORPORATE_PERMISSIONS = useMemo(
        () => ({
            [USERTYPE.OSORNOADMINISTRATOR]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showStepper: isMillOnboarding,
                    onlyGeneralInformation: !isMillOnboarding,
                    onlyMillSettings: false,
                    canEdit: true,
                    canEditProductionLines: true,
                    canEditMachineCenters: true,
                    canEditUsers: true,
                    canEditSettings: true,
                    canDelete: true,
                    showHmiCode: true,
                    showClusterCode: true,
                    canRefreshHMICode: true,
                    canRefreshClusterCode: true,
                    canDragAndDropMachineCenters: true,
                    showSecondaryCodeRequirements: true,
                },
                sidebarPermissions: {
                    showCorporateDashboardLink: true,
                    showSettingsLink: true,
                    showAPIKeyManagement: true,
                    canGenerateApiKey: true,
                    canRevokeApiKey: true,
                },
            },
            [USERTYPE.ADMINISTRATOR]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showNotifications: enabledNotifications,
                    showStepper: isMillOnboarding,
                    onlyGeneralInformation: !isMillOnboarding,
                    onlyMillSettings: false,
                    canEdit: true,
                    canEditProductionLines: true,
                    canEditMachineCenters: true,
                    canToggleMachineCenterNotifications: true,
                    canEditUsers: true,
                    canEditSettings: true,
                    canDelete: true,
                    showHmiCode: true,
                    showClusterCode: true,
                    showSecondaryCodeRequirements: true,
                    canRefreshHMICode: true,
                    canDragAndDropMachineCenters: true,
                    canRefreshClusterCode: true,
                },
                sidebarPermissions: {
                    showCorporateDashboardLink: true,
                    showSettingsLink: true,
                    showAPIKeyManagement: true,
                    canGenerateApiKey: true,
                    canRevokeApiKey: true,
                },
            },
            [USERTYPE.EXECUTIVE]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showNotifications: enabledNotifications,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                },
            },
            // API Administrator (OS-2557): its only surface is the API Key Management page, in the
            // /api_administration portal. Full key lifecycle there (generate / rotate / revoke —
            // rotate is gated by canGenerateApiKey in useApiKeyColumns), nothing anywhere else.
            // showStepper is spelled out because defaultCorporationsPermissions sets it true.
            [USERTYPE.API_ADMINISTRATOR]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showStepper: false,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                    showAPIKeyManagement: true,
                    canGenerateApiKey: true,
                    canRevokeApiKey: true,
                },
            },
            [USERTYPE.MAINTENANCE]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showNotifications: enabledNotifications,
                    showHmiCode: true,
                    showClusterCode: true,
                    canEditProductionLines: true,
                    canEditMachineCenters: true,
                    canEditUsers: false,
                    canEditSettings: false,
                    showSecondaryCodeRequirements: true,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                },
            },
            [USERTYPE.MANAGER]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showNotifications: enabledNotifications,
                    onlyMillSettings: true,
                    showStepper: false,
                    showHmiCode: true,
                    showClusterCode: true,
                    canEdit: true,
                    canEditProductionLines: true,
                    canEditMachineCenters: true,
                    canToggleMachineCenterNotifications: true,
                    canEditUsers: true,
                    canEditSettings: true,
                    showSecondaryCodeRequirements: true,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                },
            },
            [USERTYPE.OPERATIONS]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                    showNotifications: enabledNotifications,
                    showHmiCode: true,
                    showClusterCode: true,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                },
            },
            [USERTYPE.OPERATOR]: {
                corporationPermissions: {
                    ...defaultCorporationsPermissions,
                },
                sidebarPermissions: {
                    ...defaultSidebarPermissions,
                },
            },
        }),
        [isMillOnboarding, enabledNotifications],
    );

    const RCA_PERMISSIONS = useMemo(
        () => ({
            [USERTYPE.OSORNOADMINISTRATOR]: {
                ...defaultRCAPermissions,
            },
            [USERTYPE.ADMINISTRATOR]: {
                ...defaultRCAPermissions,
                issues: {
                    canCreate: true,
                    canDelete: true,
                    canEdit: true,
                    executiveMillsFilter: true,
                },
                action_plans: {
                    canCreate: true,
                    canDelete: true,
                    canEdit: true,
                    canApproveActionPlans: true,
                },
                rca_library: {
                    canCreate: true,
                    canEdit: true,
                    canDelete: true,
                    executiveMillsFilter: true,
                },
                dashboard: {
                    canCreateIssues: true,
                    canCreateRca: true,
                    canApproveActionPlans: true,
                },
                settings: {
                    canEdit: true,
                },
            },
            [USERTYPE.EXECUTIVE]: {
                ...defaultRCAPermissions,
                issues: {
                    canCreate: true,
                    canEdit: userHasOwnership,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: true,
                },
                action_plans: {
                    canCreate: true,
                    canDelete: userHasOwnership,
                    canEdit: userHasOwnership,
                    canApproveActionPlans: false,
                },
                rca_library: {
                    canCreate: true,
                    canEdit: userHasOwnership,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: true,
                },
                dashboard: {
                    canCreateIssues: true,
                    canCreateRca: true,
                    canApproveActionPlans: false,
                },
            },
            // OS-2557: no Birch/RCA access. Mapped explicitly rather than left to fall through,
            // because this lookup has no default branch and returns undefined for unmapped ids.
            [USERTYPE.API_ADMINISTRATOR]: {
                ...defaultRCAPermissions,
            },
            [USERTYPE.MAINTENANCE]: {
                ...defaultRCAPermissions,
                issues: {
                    canCreate: true,
                    canEdit: true,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: false,
                },
                action_plans: {
                    canCreate: true,
                    canDelete: userHasOwnership,
                    canEdit: true,
                    canApproveActionPlans: false,
                },
                rca_library: {
                    canCreate: true,
                    canEdit: userHasOwnership,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: false,
                },
                dashboard: {
                    canCreateIssues: true,
                    canCreateRca: true,
                    canApproveActionPlans: false,
                },
            },
            [USERTYPE.MANAGER]: {
                ...defaultRCAPermissions,
                issues: {
                    canCreate: true,
                    canEdit: true,
                    canDelete: true,
                    executiveMillsFilter: false,
                },
                action_plans: {
                    canCreate: true,
                    canDelete: true,
                    canEdit: true,
                    canApproveActionPlans: true,
                },
                rca_library: {
                    canCreate: true,
                    canEdit: true,
                    canDelete: true,
                    executiveMillsFilter: false,
                },
                dashboard: {
                    canCreateIssues: true,
                    canCreateRca: true,
                    canApproveActionPlans: true,
                },
                settings: {
                    canEdit: true,
                },
            },
            [USERTYPE.OPERATIONS]: {
                ...defaultRCAPermissions,
                issues: {
                    canCreate: true,
                    canEdit: true,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: false,
                },
                action_plans: {
                    canCreate: true,
                    canDelete: userHasOwnership,
                    canEdit: true,
                    canApproveActionPlans: false,
                },
                rca_library: {
                    canCreate: true,
                    canEdit: userHasOwnership,
                    canDelete: userHasOwnership,
                    executiveMillsFilter: false,
                },
                dashboard: {
                    canCreateIssues: true,
                    canCreateRca: true,
                    canApproveActionPlans: false,
                },
            },
            [USERTYPE.OPERATOR]: {
                ...defaultRCAPermissions,
            },
        }),
        [userHasOwnership],
    );

    return {
        CORPORATE_PERMISSIONS: CORPORATE_PERMISSIONS[currentUserType!],
        RCA_PERMISSIONS: RCA_PERMISSIONS[currentUserType!],
    };
};
export default useRolesPermissions;
